import PropTypes from 'prop-types';
// @flow

import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-bootstrap-switch';

import I18n from 'PMT/i18n/I18n';
import ApiConstants from 'PMT/constants/ApiConstants';

import classNames from 'classnames';

import { DateUtils, OrderViewUtils, NotificationUtils, StringUtils } from 'PMT/utils';
import Listener from 'PMT/flux/Listener';

import Events from 'constants/Events';
import { Config, Constants } from 'constants';
import RoutingEnum from 'constants/RoutingEnum';

import { SessionStore } from 'stores';

import { OrderAction, RouteAction, RestaurantAction } from 'actions';

// -- stores
import OrdersListStore from 'stores/OrdersListStore';

// -- entities
import { Order, OrdersList, OrderSettings } from 'PMT/entities/order';
import { Restaurant } from 'PMT/entities';


// -- views
import RestaurantMenuPage from 'pages/menu/RestaurantMenuPage';
import AbstractComponent from 'components/AbstractComponent';
import OrderRowView from './OrderRowView';
import OrderCancelModal from 'components/order/orderCancelModal/OrderCancelModal'
import OrderDetailModal from 'components/order/orderDetailModal/OrderDetailModal';
import OrderModeChooser from 'PMT/components/form/OrderModeChooser'
import Link from 'PMT/components/Link';
import OrderManufactureDelaySettingsView from '../orderManufactureDelay/OrderManufactureDelaySettingsView';
import LocalizableSelect from 'PMT/components/form/LocalizableSelect';
import TextInput from 'PMT/components/form/TextInput';

import SecurityContext from 'utils/permissions/SecurityContext';

import { ModalType } from '../constants'

export default class OrdersOnGoingListView extends AbstractComponent {

  static propTypes = {
    ordersList: PropTypes.objectOf(OrdersList).isRequired,
    restaurant: PropTypes.objectOf(Restaurant).isRequired,

    orderSettings: PropTypes.objectOf(OrderSettings).isRequired
  }

  state = {
    selectedFilter: null,
    filterName: null,
    filterPayment: null,
    filterSource: null,
    filterOrderMode: null,
    openedModalType: null,
    // order to disaply details
    selectedOrder: null,
    errorCancel: null,
    isLoadingCancelOrder: false,
    isNavigatorOnline: true,
  };

  componentDidMount() {
    OrdersListStore.addListeners(this.ordersListListeners);
    window.addEventListener('offline', () => {
     this.updateOnlineStatus(false)
    }, false)
    window.addEventListener('online', () => {
      this.updateOnlineStatus(true)
    }, false)
  }

  componentWillUnmount() {
    OrdersListStore.removeListeners(this.ordersListListeners);
  }

  get ordersListListeners(): Array<Listener> {
    return [
      new Listener(Events.UPDATE_ORDER_SUCCESS, this.onOrderUpdate),
      new Listener(Events.UPDATE_ORDER_FAILURE, this.onError)
    ];
  }

  updateOnlineStatus = status => {
    this.setState({
      isNavigatorOnline: status,
    })
  }

  onOrderUpdate = () => {
    this.onHideModal()
    this.setState({
      isLoadingCancelOrder: false,
    })
  }

  onError = () => {
    this.setState({
      isLoadingCancelOrder: false,
      errorCancel: OrdersListStore.getApiError()
    })
  }

  isFilterActive = (value) => {
    return value === this.state.selectedFilter ? 'active' : '';
  };

  onOrderSelected = (order: Order, openedModalType) => {
    this.setSelectedOrder(order, openedModalType);
  };

  handleCancelOrder = () => {
    const order = this.state.selectedOrder.copy()
    order.status = Order.OrderStatus.CANCELED

    OrderAction.updateOrder(order)
    this.setState({
      isLoadingCancelOrder: true,
    })
  }

  setSelectedOrder(selectedOrder, openedModalType) {
    this.setState({
      selectedOrder,
      openedModalType,
      errorCancel: null,
    });
  }

  toggleFilter = (filter) => {
    this.setState({
      selectedFilter: filter,
    });
  };

  setFilterName = (filterName) => {
    this.setState({
      filterName
    });
  };

  setFilterSource = (option) => {
    this.setState({
      filterSource: _.isNull(option) ? null : option.value
    });
  };

  setFilterPayment = (option) => {
    this.setState({
      filterPayment: _.isNull(option) ? null : option.value
    });
  };

  setFilterOrderMode = (mode: OrderMode) => {
    this.setState({
      filterOrderMode: mode
    });
  };

  handleDisableOrder = () => {
    const status = this.props.restaurant.status === 'ENABLED' ? 'DISABLED' : 'ENABLED';

    this.props.onUpdateRestaurantStatus(status)
  }

  //
  // Order detail modal
  //

  onHideModal = () => {
    this.setState({
      selectedOrder: null,
      openedModalType: null,
      errorCancel: null,
    });
  };

  //
  // renderers
  //

  renderOrderDetailModal() {
    if (!_.isNull(this.state.selectedOrder) && this.state.openedModalType === ModalType.DETAIL) {
      return (
        <OrderDetailModal
          order={this.state.selectedOrder}
          show
          onHide={this.onHideModal}
          currencyCode={this.props.restaurant.currency}
        />
      );
    }
  }

  renderOrderCancelModal() {
    if (!_.isNull(this.state.selectedOrder) && this.state.openedModalType === ModalType.CANCEL) {
      return (
        <OrderCancelModal
          order={this.state.selectedOrder}
          show
          isLoading={this.state.isLoadingCancelOrder}
          onHide={this.onHideModal}
          onSubmit={this.handleCancelOrder}
          errorCancel={this.state.errorCancel}
          currencyCode={this.props.restaurant.currency}
        />
      );
    }
  }

  renderOrders() {
    const onGoingOrders = this.props.ordersList.getOnGoingOrders();

    const sortedOnGoingOrders = _.sortBy(onGoingOrders, ['desc'],
    (order: Order): number => {
      return order.dueDate.valueOf();
    });


    // we need to stringify/parse filterSource in order to match label/value pair in LocalizableSelect
    const filteredOnGoingOrder = sortedOnGoingOrders.filter((order) => {
      return (
        (_.isEmpty(this.state.filterName) || (!_.isNil(order.user) && StringUtils.searchStringOn(this.state.filterName, `${order.user.firstName} ${order.user.lastName}`)))
        && (_.isEmpty(this.state.filterPayment) || this.state.filterPayment == order.isPayed().toString())
        && (_.isEmpty(this.state.filterSource) || _.find(JSON.parse(this.state.filterSource), (source) => { return source == order.source.platform }))
        && (_.isNull(this.state.filterOrderMode) || this.state.filterOrderMode === order.mode)
      )
    });

    const platform = [];
    _.forEach(ApiConstants.Platform, (value, label) => {
      if (label == ApiConstants.Platform.IOS) {
        platform.push({ label: OrderViewUtils.getLabelForPlatform(label), value: JSON.stringify([ value, ApiConstants.Platform.ANDROID ]) });
      } else if (label != ApiConstants.Platform.ANDROID) {
        platform.push({ label: OrderViewUtils.getLabelForPlatform(label), value: JSON.stringify([ value ]) });
      }
    });

    // create dates for calculs
    const manufactureDelay = this.props.orderSettings.manufactureDelay;

    const rows = filteredOnGoingOrder.map((order: Order) => {

      const selected = !_.isNull(this.state.selectedOrder)
      && this.state.selectedOrder.id === order.id;

      const delay = this.props.orderSettings.getOrderDelay(order.mode);

      const priority = order.getPriority(delay);

      return (
        <OrderRowView
          order={order}
          key={order.id}
          onOrdersListChange={this.onOrderChange}
          onSelected={this.onOrderSelected}
          selected={selected}
          priority={priority}
          currencyCode={this.props.restaurant.currency}
        />
      );
    });

    return (
      <div className="table_container table-responsive">
        <table className="orders_ready table table-striped table-hover">
          <thead>
            <tr>
              <th></th>
              <th className="text-left">
                <i className="fa fa-clock-o" />
              </th>
              {/* TODO : use new source method
              <th>
                <LocalizableSelect
                  name="filterSource"
                  placeholder={I18n.tr('order.on_going.table.header.source')}
                  value={this.state.filterSource}
                  options={platform}
                  onChange={this.setFilterSource}
                  multi={false}
                />
              </th>
            */}
              <th>
                <OrderModeChooser
                  placeholder={I18n.tr('order.on_going.table.header.type')}
                  onChange={this.setFilterOrderMode}
                  multi={false}
                  value={this.state.filterOrderMode}
                  modes={_.values(Order.OrderMode)}
                />
              </th>
              <th className="filters">
                <TextInput
                  type="text"
                  value={this.state.filterName}
                  placeholder={I18n.tr('order.on_going.table.header.name')}
                  className={this.isFilterActive('name')}
                  onFocus={() => { this.toggleFilter('name')}}
                  onBlur={() => { this.toggleFilter('')}}
                  onChange={(value, event) => this.setFilterName(value)}
                />
                <i className="fa fa-search"></i>
              </th>
              <th
                className="hidden-xs hidden-sm hidden-md"
              >
                {I18n.tr('order.on_going.table.header.phone')}
              </th>
              <th>
                {I18n.tr('order.on_going.table.header.total')}
              </th>
              <th>
                <LocalizableSelect
                  name="filterPayment"
                  placeholder={I18n.tr('order.on_going.table.header.payment')}
                  value={this.state.filterPayment}
                  options={[{
                    label: I18n.tr('order.on_going.on_site'),
                    value: 'false'
                  }, {
                    label: I18n.tr('order.on_going.online'),
                    value: 'true'
                  }]}
                  onChange={this.setFilterPayment}
                  multi={false}
                />
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {!this.state.isNavigatorOnline && (
              <tr><td colSpan={9} style={{ padding: 0 }}>
                <div className="alert-danger text-center" style={{ padding: '12px' }}>
                  {I18n.tr('order.on_going.table.offline')}
                </div>
              </td></tr>
            )}
            {rows}
          </tbody>
        </table>
      </div>
    );
  }

  runSound = () => {
    // init bunch of sounds
    ion.sound({
      sounds: [
        {name: "new_order_notification"},
      ],

      // main config
      path: 'public/sounds/',
      preload: true,
      multiplay: true,
      volume: 1
    });

    // play sound
    ion.sound.play("new_order_notification");
  };

  render(): Object {
    const ordersList: OrdersList = this.props.ordersList;

    let contentView = (null);

    if (_.isNull(ordersList)) {
      contentView = this.renderLoading();
    } else {
      contentView = (
        <div className="order_list__content orders_on_going">

          <div className="row">

            {!this.props.restaurant.status === 'DISABLED' && (
              <div className="col-xs-12 top-buffer-20">
                <div className="alert alert-danger">
                  {SecurityContext.restaurantSettings.editConfigurationGeneralActivation() ? (
                    I18n.tr('order.on_going.order_disabled_text')
                  ) : (
                    I18n.tr('order.on_going.order_disabled_text_without_permission')
                  )}
                </div>
              </div>
            )}

            <div className="col-xs-12 col-lg-6">

              <OrderManufactureDelaySettingsView
                orderSettings={this.props.orderSettings}
              />

            </div>

            <div className="col-xs-12 col-lg-6 top-buffer-10 order_list__settings">
              {(
                SecurityContext.restaurantMenu.canEdit() ||
                SecurityContext.restaurantMenu.canEditAvailabilityProducts()
              ) && (
                <Link
                  to={RoutingEnum.RESTAURANT_MENU}
                  query={{ tab: RestaurantMenuPage.Tabs.PRODUCTS }}
                  target="_blank"
                >
                  <button className="btn btn-default btn-with-link order_list__disable_item">
                    {I18n.tr('order.on_going.disable_product')}
                  </button>
                </Link>
              )}

              {this.props.restaurant.status !== 'ARCHIVED' && SecurityContext.restaurantSettings.editConfigurationGeneralActivation() &&
                <div className="order_list__disable_order">
                  <span className="order_list__disable_order__text text-left">
                    {I18n.tr('order.on_going.disable_order')}
                  </span>
                  <Switch
                    value={this.props.restaurant.status === 'ENABLED'}
                    onChange={this.handleDisableOrder}
                    bsSize="mini"
                    onColor="default"
                    onText={I18n.tr('order.on_going.order_enabled')}
                    offColor="danger"
                    offText={I18n.tr('order.on_going.order_disabled')}
                  />
                </div>
              }
            </div>


          {/* {Config.IS_DEV &&
            <div>
            <button onClick={this.runSound} className="btn">
            TEST NOTIFICATION
            </button>
            </div>
          } */}

            <div className="col-xs-12 order_list__list">
              {this.renderOrders()}
            </div>
          </div>

          {this.renderOrderDetailModal()}
          {this.renderOrderCancelModal()}
        </div>
      );
    }

    return (
      <div className="order_list">
        {this.renderLoading()}
        {contentView}
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./scripts/pages/order/ordersList/ordersOnGoing/OrdersOnGoingListView.js